import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUpRight, Star, Trophy, Award, ShieldCheck } from 'lucide-react';
import Reveal from '../components/Reveal';
import CounterStat from '../components/CounterStat';
import Building3D from '../components/Building3D';
import {
  COMPANY, HERO, SERVICES, SECTORS, STATS,
  PROJECTS, TESTIMONIALS, NEWS, PARTNERS,
} from '../data/mock';

export default function Home() {
  const [parallax, setParallax] = useState(0);
  useEffect(() => {
    const onScroll = () => setParallax(window.scrollY);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="bg-[var(--bg)]">
      {/* HERO */}
      <section className="relative min-h-[100svh] bg-[var(--navy)] text-white overflow-hidden">
        {/* Wireframe grid background */}
        <div className="absolute inset-0 wireframe-bg opacity-50" />
        {/* Animated wireframe building */}
        <div
          className="absolute right-[-40px] md:right-0 top-[60px] md:top-[40px] w-[120%] md:w-[60%] h-[80%]"
          style={{ transform: `translateY(${parallax * 0.08}px)` }}
        >
          <Building3D />
        </div>
        {/* dark gradient mask */}
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy)] via-[var(--navy)]/60 to-transparent" />

        <div className="relative container-rs pt-44 lg:pt-56 pb-24">
          <div className="flex items-center gap-4">
            <span className="coral-dot pulse-coral" />
            <span className="font-tag opacity-80">{COMPANY.tagline}</span>
          </div>
          <Reveal>
            <h1 className="font-display text-[15vw] md:text-[10vw] lg:text-[170px] leading-[0.88] tracking-[-0.03em] mt-8 max-w-[1200px]">
              Built from a<br />
              <span className="font-display-italic text-[var(--coral)]">different perspective.</span>
            </h1>
          </Reveal>
          <Reveal delay={150}>
            <p className="mt-10 max-w-2xl text-[16px] md:text-[18px] leading-relaxed opacity-85">
              {HERO.sub}
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/services" className="pill pill-coral"><span>Our Services</span><ArrowUpRight size={14} /></Link>
              <Link to="/projects" className="pill pill-outline-light"><span>View Projects</span><ArrowUpRight size={14} /></Link>
            </div>
          </Reveal>
          <div className="mt-20 flex flex-wrap gap-2">
            {HERO.awards.map((a) => (
              <span key={a} className="award-pill text-white/75 hover:text-[var(--coral)] hover:border-[var(--coral)] transition-colors">
                <Trophy size={12} /> {a}
              </span>
            ))}
          </div>

          <div className="absolute bottom-8 left-0 right-0 container-rs flex items-end justify-between text-white/70">
            <div className="flex items-center gap-3 font-tag">
              <ArrowDown size={14} />
              <span>Scroll to explore</span>
            </div>
            <div className="hidden md:flex items-center gap-6 font-tag">
              <span>{COMPANY.region}</span>
              <span className="opacity-60">|</span>
              <a href={`tel:${COMPANY.phoneTel}`} className="u-link">{COMPANY.phone}</a>
            </div>
          </div>
        </div>

        {/* Vertical edge text */}
        <div className="hidden lg:block absolute top-32 left-6 [writing-mode:vertical-rl] font-tag opacity-60 tracking-[0.4em]">
          — BSP / 001 / {new Date().getFullYear()}
        </div>
      </section>

      {/* SECTOR ticker */}
      <section className="bg-[var(--navy)] text-white border-t border-white/10 overflow-hidden">
        <div className="flex marquee-track py-8" style={{ minWidth: '200%' }}>
          {[...SECTORS, ...SECTORS, ...SECTORS].map((s, i) => (
            <span key={i} className="font-display text-3xl md:text-5xl px-10 flex items-center gap-10 whitespace-nowrap">
              {s}
              <span className="text-[var(--coral)] text-xl">+</span>
            </span>
          ))}
        </div>
      </section>

      {/* INTRO + Services split */}
      <section className="py-28 md:py-36 bg-[var(--bg)]">
        <div className="container-rs">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <Reveal className="md:col-span-7">
              <span className="section-index"><span className="coral-dot" /> 002 — Our Services</span>
              <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.95]">
                Two studios. <span className="font-display-italic text-[var(--coral)]">One standard.</span>
              </h2>
            </Reveal>
            <Reveal delay={150} className="md:col-span-4 md:col-start-9">
              <p className="text-[15px] text-[var(--ink-mute)] leading-relaxed">
                Based centrally in Shropshire, we offer nationwide construction services to a host of different industry sectors as specialists in both interior fit-out and residential new build &amp; conversion.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-20">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 120}>
                <Link to={`/services/${s.id}`} className="group block soft-card overflow-hidden tilt">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.image} alt={s.title} className="zoom-img absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/80 via-transparent to-transparent" />
                    <div className="absolute top-6 left-6 font-tag text-white/80">{s.code} — {s.tag}</div>
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white">
                      <div className="font-display text-5xl md:text-6xl">{s.title}</div>
                      <div className="w-12 h-12 rounded-full bg-[var(--coral)] flex items-center justify-center group-hover:scale-110 transition-transform">
                        <ArrowUpRight size={18} />
                      </div>
                    </div>
                  </div>
                  <div className="p-8 md:p-10">
                    <p className="text-[var(--ink-mute)] text-[15px] leading-relaxed">{s.short}</p>
                    <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
                      {s.bullets.slice(0, 4).map((b) => (
                        <li key={b} className="flex items-start gap-2"><span className="w-1.5 h-1.5 mt-2 rounded-full bg-[var(--coral)]" />{b}</li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECT */}
      <section className="py-28 md:py-36 bg-[var(--bg-soft)]">
        <div className="container-rs">
          <div className="flex items-end justify-between gap-8 flex-wrap mb-12">
            <div>
              <span className="section-index"><span className="coral-dot" /> 003 — Featured Project</span>
              <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">
                Boulevard, <span className="font-display-italic text-[var(--coral)]">Bristol Street.</span>
              </h2>
            </div>
            <Link to="/projects" className="font-tag u-link">View all projects —</Link>
          </div>

          <Reveal>
            <div className="relative group overflow-hidden rounded-3xl">
              <img src={PROJECTS[0].image} alt={PROJECTS[0].title} className="w-full h-[480px] md:h-[680px] object-cover zoom-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)] via-[var(--navy)]/30 to-transparent" />
              <div className="absolute bottom-10 left-10 right-10 text-white grid md:grid-cols-12 gap-8 items-end">
                <div className="md:col-span-6">
                  <div className="font-tag opacity-80">{PROJECTS[0].sector} · {PROJECTS[0].location}</div>
                  <div className="font-display text-4xl md:text-6xl mt-3">{PROJECTS[0].title}</div>
                </div>
                <div className="md:col-span-4 md:col-start-7 grid grid-cols-3 gap-4">
                  <Stat l="Units" v={PROJECTS[0].units} />
                  <Stat l="Value" v={PROJECTS[0].value} />
                  <Stat l="Year" v={PROJECTS[0].year} />
                </div>
                <div className="md:col-span-2 md:col-start-11 flex justify-end">
                  <Link to={`/projects/${PROJECTS[0].id}`} className="pill pill-coral"><span>Case Study</span><ArrowUpRight size={14} /></Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 bg-[var(--navy)] text-white">
        <div className="container-rs grid grid-cols-2 lg:grid-cols-4 gap-12">
          {STATS.map((s) => <CounterStat key={s.label} {...s} />)}
        </div>
      </section>

      {/* PROJECT GRID PREVIEW */}
      <section className="py-28 md:py-36 bg-[var(--bg)]">
        <div className="container-rs">
          <div className="flex items-end justify-between gap-8 flex-wrap mb-16">
            <div>
              <span className="section-index"><span className="coral-dot" /> 004 — Selected Work</span>
              <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">A portfolio of <span className="font-display-italic text-[var(--coral)]">delivered</span> ambition.</h2>
            </div>
            <Link to="/projects" className="pill pill-outline-dark"><span>Browse all</span><ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-2 gap-8 md:gap-x-10 md:gap-y-20">
            {PROJECTS.slice(1, 5).map((p, i) => (
              <Reveal key={p.id} delay={(i % 2) * 80} className={`group proj-card ${i % 2 === 1 ? 'md:translate-y-24' : ''}`}>
                <Link to={`/projects/${p.id}`} className="block">
                  <div className="overflow-hidden rounded-2xl">
                    <img src={p.image} alt={p.title} className="proj-img w-full h-[460px] md:h-[560px] object-cover" />
                  </div>
                  <div className="flex items-start justify-between mt-6">
                    <div>
                      <div className="font-tag text-[var(--ink-mute)]">{p.sector} · {p.location}</div>
                      <div className="font-display text-3xl md:text-4xl mt-2 group-hover:text-[var(--coral)] transition-colors">{p.title}</div>
                      <div className="text-[14px] text-[var(--ink-mute)] mt-2 max-w-md">{p.summary}</div>
                    </div>
                    <div className="font-tag text-[var(--ink-mute)] whitespace-nowrap">{p.code} — {p.year}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-28 md:py-36 bg-[var(--navy)] text-white">
        <div className="container-rs">
          <span className="section-index text-white/70"><span className="coral-dot" /> 005 — Reputation</span>
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl mt-8 leading-[1.1] max-w-5xl">
              “The best conversion project we have, <span className="font-display-italic text-[var(--coral)]">to date,</span> been involved in.”
            </h2>
          </Reveal>
          <div className="mt-12 flex items-center gap-5">
            <div className="w-14 h-14 rounded-full bg-[var(--coral)] flex items-center justify-center font-display text-2xl">I</div>
            <div>
              <div className="font-display text-2xl">Investin PLC</div>
              <div className="font-tag opacity-70">Sapphire Court · London</div>
            </div>
            <div className="ml-auto flex items-center gap-1">
              {[1,2,3,4,5].map((n) => <Star key={n} size={18} className="fill-[var(--coral)] text-[var(--coral)]" />)}
            </div>
          </div>
          <div className="mt-12">
            <Link to="/testimonials" className="pill pill-outline-light"><span>All Testimonials</span><ArrowUpRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* NEWS */}
      <section className="py-28 md:py-36 bg-[var(--bg)]">
        <div className="container-rs">
          <div className="flex items-end justify-between gap-8 flex-wrap mb-12">
            <div>
              <span className="section-index"><span className="coral-dot" /> 006 — Read all about it</span>
              <h2 className="font-display text-5xl md:text-7xl mt-6 leading-[0.95]">From the <span className="font-display-italic text-[var(--coral)]">studio.</span></h2>
            </div>
            <Link to="/news" className="font-tag u-link">All news —</Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {NEWS.slice(0, 3).map((n, i) => (
              <Reveal key={n.id} delay={i * 100} className="group">
                <div className="overflow-hidden rounded-2xl">
                  <img src={n.image} alt={n.title} className="zoom-img w-full h-[320px] object-cover" />
                </div>
                <div className="mt-5 font-tag text-[var(--ink-mute)] flex items-center gap-3">
                  <span className="coral-dot" />
                  {n.category} · {new Date(n.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <h3 className="font-display text-2xl md:text-3xl mt-3 group-hover:text-[var(--coral)] transition-colors">{n.title}</h3>
                <p className="mt-2 text-[14px] text-[var(--ink-mute)] leading-relaxed">{n.excerpt}</p>
                <Link to="/news" className="inline-flex items-center gap-2 mt-4 font-tag u-link">Read more <ArrowUpRight size={12} /></Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-20 bg-[var(--bg-soft)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> 007 — Who we work with</span>
          <div className="mt-10 flex flex-wrap items-center gap-x-12 gap-y-6">
            {PARTNERS.map((p) => (
              <span key={p} className="font-display text-3xl md:text-4xl text-[var(--ink)]/50 hover:text-[var(--coral)] transition-colors cursor-default">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 md:py-44 bg-[var(--navy)] text-white overflow-hidden">
        <div className="absolute inset-0 wireframe-bg opacity-30" />
        <div className="relative container-rs">
          <span className="section-index text-white/70"><span className="coral-dot" /> 008 — Begin</span>
          <Reveal>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-8 leading-[0.95] max-w-4xl">
              Have a build in mind? <span className="font-display-italic text-[var(--coral)]">Let's talk.</span>
            </h2>
          </Reveal>
          <p className="mt-8 max-w-2xl opacity-80 leading-relaxed">
            From a Cat B fit-out to a 400-unit BtR scheme, every conversation starts the same way — a coffee, a sketch, and a clear plan.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" className="pill pill-coral"><span>Start a Project</span><ArrowUpRight size={14} /></Link>
            <a href={`tel:${COMPANY.phoneTel}`} className="pill pill-outline-light"><span>Call {COMPANY.phone}</span><ArrowUpRight size={14} /></a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ l, v }) {
  return (
    <div className="border-l border-white/30 pl-4">
      <div className="font-tag opacity-70">{l}</div>
      <div className="font-display text-2xl mt-1">{v ?? '—'}</div>
    </div>
  );
}
